import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Email as EmailIcon,
  Image as ImageIcon,
  Link as LinkIcon,
  Sms as SmsIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment-timezone';
import { apiClient } from '../../services/api';
import {
  applyMissingAgentStatusPdfCellStyle,
  getMissingAgentChipLabel,
  getMissingAgentSecondaryText,
  getMissingAgentShiftPreview,
  MissingAgentRow,
} from '../../utils/attendanceLabels';
import MissingAgentsShareContent from './MissingAgentsShareContent';
import NotificationRecipientFields from '../coverage/NotificationRecipientFields';

interface ShareMissingAgentsModalProps {
  open: boolean;
  onClose: () => void;
  agents: MissingAgentRow[];
  workDate: string;
}

type ShareAction = 'image' | 'pdf' | 'email' | 'sms' | 'link' | null;

const ShareMissingAgentsModal: React.FC<ShareMissingAgentsModalProps> = ({
  open,
  onClose,
  agents,
  workDate,
}) => {
  const captureRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [activeAction, setActiveAction] = useState<ShareAction>(null);
  const [shareUrl, setShareUrl] = useState('');
  const [deliveryStatus, setDeliveryStatus] = useState<any>(null);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [smsRecipients, setSmsRecipients] = useState('');
  const [subject, setSubject] = useState('');
  const [note, setNote] = useState('');
  const [defaultPhone, setDefaultPhone] = useState('');
  const generatedAt = useMemo(() => new Date().toISOString(), [open]);

  useEffect(() => {
    if (!open) return;
    setActiveAction(null);
    setShareUrl('');
    setNote('');
    setSubject(`TEKS TIME Missing Agents - ${workDate}`);
    Promise.all([
      apiClient.notificationSettings.getStatus(),
      apiClient.userNotificationPreferences.get().catch(() => null),
      apiClient.dashboard.previewMissingAgentsShare({ agents, workDate }),
    ]).then(([statusRes, prefsRes, previewRes]) => {
      setDeliveryStatus(statusRes.data.data);
      const phone = prefsRes?.data?.data?.phoneNumber;
      if (phone) {
        setDefaultPhone(phone);
        setSmsRecipients(phone);
      }
      if (previewRes.data?.data?.defaultSubject) {
        setSubject(previewRes.data.data.defaultSubject);
      }
    }).catch(() => setDeliveryStatus(null));
  }, [open, agents, workDate]);

  const previewNames = agents.slice(0, 3).map((agent) => agent.name).join(', ');
  const hasAgents = agents.length > 0;

  const ensureShareLink = async () => {
    if (shareUrl) return shareUrl;
    const response = await apiClient.dashboard.createMissingAgentsShare({ agents, workDate });
    const url = response.data.data.shareUrl;
    setShareUrl(url);
    return url;
  };

  const handleCopyImage = async () => {
    if (!captureRef.current) return;
    try {
      setLoading(true);
      const canvas = await html2canvas(captureRef.current, {
        backgroundColor: '#f4f6f8',
        scale: 2,
        useCORS: true,
      });
      canvas.toBlob(async (blob) => {
        if (!blob) throw new Error('Failed to generate image');
        try {
          await navigator.clipboard.write([
            new ClipboardItem({ 'image/png': blob }),
          ]);
          toast.success('Missing agents image copied to clipboard');
        } catch {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `missing-agents-${workDate}.png`;
          link.click();
          URL.revokeObjectURL(url);
          toast.success('Image downloaded (clipboard unavailable)');
        }
      }, 'image/png');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to copy image');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setLoading(true);
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('TEKS TIME - Missing Agents', 14, 16);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Date: ${workDate}`, 14, 24);
      doc.text(`Generated: ${moment(generatedAt).format('MMM D, YYYY h:mm A')}`, 14, 30);
      doc.text(`Total missing agents: ${agents.length}`, 14, 36);
      doc.text('Grace 10 min · Slightly Late · Late 15 min · no call/no show 30 min', 14, 42);
      doc.text('(auto unless supervisor approves absence)', 14, 47);

      autoTable(doc, {
        startY: 54,
        head: [[
          'Employee',
          'Campaign',
          'Department',
          'Scheduled',
          'Status',
          'Details',
        ]],
        body: agents.map((agent) => [
          agent.name,
          agent.campaign,
          agent.department,
          getMissingAgentShiftPreview(agent) || 'N/A',
          getMissingAgentChipLabel(agent),
          getMissingAgentSecondaryText(agent),
        ]),
        styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
        headStyles: { fillColor: [26, 35, 126], textColor: [255, 255, 255], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        margin: { left: 14, right: 14 },
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index === 4) {
            const agent = agents[data.row.index];
            if (agent) {
              applyMissingAgentStatusPdfCellStyle(data.cell as any, agent);
            }
          }
        },
      });

      const pageCount = doc.getNumberOfPages();
      for (let page = 1; page <= pageCount; page += 1) {
        doc.setPage(page);
        doc.setFontSize(8);
        doc.setTextColor(120);
        doc.text('Generated from TEKS TIME Dashboard', 14, doc.internal.pageSize.getHeight() - 8);
      }

      doc.save(`missing-agents-${workDate}.pdf`);
      toast.success('PDF downloaded');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to download PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      setLoading(true);
      const url = await ensureShareLink();
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied to clipboard');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to copy link');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!deliveryStatus?.emailReady) {
      toast.error('Email service is not configured.');
      return;
    }
    const recipients = emailRecipients.split(',').map((value) => value.trim()).filter(Boolean);
    if (recipients.length === 0) {
      toast.error('Enter at least one email recipient');
      return;
    }
    try {
      setLoading(true);
      const response = await apiClient.dashboard.emailMissingAgentsShare({
        agents,
        workDate,
        recipients,
        subject,
        message: note || undefined,
      });
      if (response.data.success) {
        toast.success(response.data.message || 'Email sent');
        onClose();
      } else {
        toast.warning(response.data.message || 'Email could not be sent');
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  const handleSendSms = async () => {
    if (!deliveryStatus?.smsReady) {
      toast.error('SMS service is not configured.');
      return;
    }
    const smsList = smsRecipients.split(',').map((value) => value.trim()).filter(Boolean);
    if (smsList.length === 0) {
      toast.error('Enter at least one SMS recipient');
      return;
    }
    try {
      setLoading(true);
      const response = await apiClient.dashboard.smsMissingAgentsShare({
        agents,
        workDate,
        smsRecipients: smsList,
        message: note || undefined,
      });
      if (response.data.success) {
        toast.success(response.data.message || 'SMS sent');
        onClose();
      } else {
        toast.warning(response.data.message || 'SMS could not be sent');
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to send SMS');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Share Missing Agents</DialogTitle>
      <DialogContent>
        {!hasAgents ? (
          <Alert severity="info" sx={{ mt: 1 }}>No missing agents to share.</Alert>
        ) : (
          <>
            <Box sx={{ mt: 1, mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2">Preview</Typography>
              <Typography variant="body2" color="text.secondary">
                {agents.length} missing agent{agents.length === 1 ? '' : 's'} · Generated {moment(generatedAt).format('h:mm A')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {previewNames}{agents.length > 3 ? ` +${agents.length - 3} more` : ''}
              </Typography>
            </Box>

            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Button fullWidth variant="outlined" startIcon={<ImageIcon />} onClick={handleCopyImage} disabled={loading}>
                  Copy as Image
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Button fullWidth variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadPdf} disabled={loading}>
                  Download PDF
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Button fullWidth variant="outlined" startIcon={<LinkIcon />} onClick={handleCopyLink} disabled={loading}>
                  Copy Link
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Button
                  fullWidth
                  variant={activeAction === 'email' ? 'contained' : 'outlined'}
                  startIcon={<EmailIcon />}
                  onClick={() => setActiveAction(activeAction === 'email' ? null : 'email')}
                  disabled={loading}
                >
                  Email Summary
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Button
                  fullWidth
                  variant={activeAction === 'sms' ? 'contained' : 'outlined'}
                  startIcon={<SmsIcon />}
                  onClick={() => setActiveAction(activeAction === 'sms' ? null : 'sms')}
                  disabled={loading}
                >
                  SMS Summary
                </Button>
              </Grid>
            </Grid>

            {activeAction === 'email' && (
              <Box sx={{ mb: 2 }}>
                {!deliveryStatus?.emailReady && (
                  <Alert severity="warning" sx={{ mb: 2 }}>Email service is not configured.</Alert>
                )}
                <NotificationRecipientFields
                  channel="email"
                  emailRecipients={emailRecipients}
                  smsRecipients=""
                  onEmailRecipientsChange={setEmailRecipients}
                  onSmsRecipientsChange={() => undefined}
                  defaultPhone={defaultPhone}
                />
                <TextField
                  fullWidth
                  label="Subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  sx={{ mt: 2 }}
                />
                <TextField
                  fullWidth
                  label="Optional note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  multiline
                  minRows={2}
                  sx={{ mt: 2 }}
                />
                <Button variant="contained" sx={{ mt: 2 }} onClick={handleSendEmail} disabled={loading}>
                  Send Email
                </Button>
              </Box>
            )}

            {activeAction === 'sms' && (
              <Box sx={{ mb: 2 }}>
                {!deliveryStatus?.smsReady && (
                  <Alert severity="warning" sx={{ mb: 2 }}>SMS service is not configured.</Alert>
                )}
                <NotificationRecipientFields
                  channel="sms"
                  emailRecipients=""
                  smsRecipients={smsRecipients}
                  onEmailRecipientsChange={() => undefined}
                  onSmsRecipientsChange={setSmsRecipients}
                  defaultPhone={defaultPhone}
                />
                <TextField
                  fullWidth
                  label="Optional note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  multiline
                  minRows={2}
                  sx={{ mt: 2 }}
                  helperText="SMS includes a short alert and link to the full list."
                />
                <Button variant="contained" sx={{ mt: 2 }} onClick={handleSendSms} disabled={loading}>
                  Send SMS
                </Button>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Full list preview</Typography>
            <Box sx={{ maxHeight: 320, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
              <MissingAgentsShareContent
                agents={agents}
                workDate={workDate}
                generatedAt={generatedAt}
                width="100%"
              />
            </Box>
          </>
        )}

        <Box
          aria-hidden
          sx={{
            position: 'fixed',
            left: '-10000px',
            top: 0,
            width: 720,
            pointerEvents: 'none',
            opacity: 1,
          }}
        >
          <MissingAgentsShareContent
            ref={captureRef}
            agents={agents}
            workDate={workDate}
            generatedAt={generatedAt}
            width={720}
            forExport
          />
        </Box>
      </DialogContent>
      <DialogActions>
        {loading && <CircularProgress size={20} sx={{ mr: 1 }} />}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareMissingAgentsModal;
